import type { AlignmentStatus, OccupationGroupAlignment } from '../../types/metro';
import { ratioColor } from '../../lib/map-utils';
import { formatRatio } from '../../lib/utils';
import { cn } from '../../lib/utils';

/**
 * Title-cased status labels for aria announcements. Mirrors the
 * shared status-label vocabulary so screen readers hear the same
 * phrasing across the chart, the table, and the popup.
 */
const STATUS_LABEL: Record<AlignmentStatus, string> = {
  shortage: 'Shortage',
  surplus: 'Surplus',
};

/**
 * Horizontal bar chart of credentials-to-jobs ratios per occupational
 * group inside the selected metro.
 *
 * Each bar:
 *   - Label on the left (occupational group name, truncated if long).
 *   - Bar length: ratio scaled against the visible group with the
 *     largest ratio (or 1.0 if all are below parity) so even a metro
 *     where every group is in deep shortage still produces readable
 *     bar variation.
 *   - Color: `ratioColor(ratio)` — continuous rose → gold → green
 *     gradient anchored at 0.0 / 1.0 / 1.8, matching the Map page
 *     and Rankings severity pill so the encoding is consistent
 *     across the tool.
 *   - Numeric ratio printed at the bar's tail (2-decimal, tabular).
 *
 * Sort: ratio ascending — worst shortage first, so the user's eye
 * lands on the most actionable group at the top of the panel.
 *
 * If `groups` is empty (filters cleared all rows) we render a small
 * "No groups match the current filters" empty state.
 */

const PARITY_REFERENCE = 1.0;

interface RatioChartProps {
  readonly groups: ReadonlyArray<OccupationGroupAlignment>;
}

export function RatioChart({ groups }: RatioChartProps) {
  // Sort ascending: worst shortage on top. Cloned so we don't mutate
  // the caller's array — `groups` is typed readonly but defensive
  // copying keeps the contract explicit.
  const sorted = [...groups].sort((a, b) => a.ratio - b.ratio);

  // Scale: the widest bar fills ~95% of the track. Use the larger of
  // (max visible ratio, parity) as the denominator so a metro where
  // every group is below 1.0 still gets a sensible spread instead of
  // one tiny bar.
  //
  // TODO(DEC-pending): the per-metro relative scale can over-emphasize
  // outliers — e.g. one group at ratio 5.3 dominates the bar width
  // budget and a 0.5 group ends up at ~9% visual width even though it
  // is in moderate shortage. Maria + Luis to decide whether to switch
  // to a fixed 0–2 scale (with an outlier indicator for ratios > 2)
  // so cross-metro readings are visually comparable. Backlog item
  // from PR #51 code review.
  const maxRatio = sorted.reduce(
    (max, g) => (Number.isFinite(g.ratio) && g.ratio > max ? g.ratio : max),
    PARITY_REFERENCE,
  );

  return (
    // Cap the panel at the viewport height (minus the topbar +
    // section-nav + filter-bar offsets) so it never grows taller than
    // the fold. The bars list inside scrolls when content overflows
    // this cap. The header stays pinned to the top of the panel via
    // `flex-col` because the list (not the panel) is what scrolls.
    <div className="flex h-full max-h-[calc(100vh_-_var(--offset-filter-stack)_-_4px)] flex-col overflow-hidden rounded-lg border border-border bg-white p-5">
      <div className="mb-1 flex-none">
        <span className="block text-[11px] font-semibold uppercase tracking-wider text-text-4">
          Alignment
        </span>
        <h2 className="text-base font-bold text-navy">Credentials-to-jobs ratio</h2>
        {/* 2026-07-02 (V2 feedback): reviewers found the "One bar per
            occupational group. Sorted by ratio — worst shortage on top."
            caption confusing (three ideas crammed into one line).
            Simplified to the sort-order fact alone. */}
        <p className="mt-1 text-xs leading-[1.5] text-text-3">
          Ratios are sorted from most to least severe.
        </p>
      </div>
      {sorted.length === 0 ? (
        <div className="mt-4 flex flex-1 items-center justify-center">
          <p className="text-sm text-text-3">No groups match the current filters.</p>
        </div>
      ) : (
        <ul
          // `justify-start` so the bars hug the top of the panel.
          // `overflow-y-auto` so the list scrolls internally when
          // it exceeds the panel's max-height (set on the outer
          // wrapper above) — the header stays put, only the bars
          // scroll. `-mr-2 pr-2` reserves space for the scrollbar
          // so the rightmost ratio numbers don't shift when the
          // scrollbar appears/disappears.
          className="-mr-2 mt-4 flex flex-1 flex-col justify-start gap-3 overflow-y-auto pr-2"
          aria-label="Credentials-to-jobs ratio by occupational group"
        >
          {sorted.map((g) => {
            const safeRatio = Number.isFinite(g.ratio) ? g.ratio : 0;
            const widthPct = Math.max(
              2,
              Math.min(100, (safeRatio / maxRatio) * 95),
            );
            const color = ratioColor(safeRatio);
            return (
              <li
                key={g.name}
                className="flex items-center gap-3"
                aria-label={`${g.name}: ratio ${formatRatio(safeRatio)}, status ${STATUS_LABEL[g.status]}`}
              >
                <span
                  className={cn(
                    'w-28 shrink-0 truncate text-[11px] font-medium text-text-2',
                  )}
                  title={g.name}
                >
                  {g.name}
                </span>
                <span className="relative h-2.5 flex-1 rounded bg-border/40">
                  <span
                    className="absolute left-0 top-0 h-full rounded"
                    style={{ width: `${widthPct}%`, background: color }}
                    aria-hidden="true"
                  />
                </span>
                <span className="w-10 shrink-0 text-right font-mono text-[11px] tabular-nums text-text-2">
                  {formatRatio(safeRatio)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
